import React, { useEffect, useState } from 'react';
import { Button, Modal, Space, Spin, Table, Tag, Typography, message } from 'antd';

import { useTagContext } from '../../../core/components/tags/useTagContext';
import moment from 'moment';
import { formatWholeNumberNaN } from '../../../core/utils/campaigns';
import { TagOutlined } from '@ant-design/icons';

const { Text } = Typography;

const typePicker = (date, value) => {
  let color = 'success';
  if (!date) {
    color = 'secondary';
  } else if (date && value < 1) {
    color = 'danger';
  }

  return color;
};

const colorPicker = (date, value) => {
  let color = 'success';
  if (!date) {
    color = 'default';
  } else if (date && value < 1) {
    color = 'error';
  }

  return color;
};

const CampaignRequestorTagsDialog = ({ visible, setVisible, form, eventType, onAddSelections }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const {
    fetchTags,
    tags,
    loadingTags,
    getBasicTagActivity,
    basicTagActivity,
    tagActivityLoading,
    tagActivityError,
    clearBasicTagActivity,
  } = useTagContext();

  const handleTestSignal = ({ appId, orgId, tagId }) => {
    getBasicTagActivity({
      variables: {
        appId: Array.isArray(appId) ? appId[0] : appId,
        orgId,
        tagId,
      },
    });
  };

  useEffect(() => {
    if (basicTagActivity) {
      message.success('Signal Tested Successfully', 3);
      onFetchTags();
      clearBasicTagActivity();
    } else if (basicTagActivity == "") {
      message.error('Error Testing Signal: cannot fetch tag activity');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicTagActivity]);

  useEffect(() => {
    if (tagActivityError) {
      message.error(tagActivityError.message || 'Error fetching signal', 3);
    }
  }, [tagActivityError]);

  const columns = [
    {
      title: 'Tag Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true, // Enables ellipsis for text overflow
      width: 200, // Set a fixed width for the column
    },
    {
      title: 'Advertiser',
      dataIndex: 'orgName',
      key: 'orgName',
      ellipsis: true, // Enables ellipsis for text overflow
      width: 200, // Set a fixed width for the column
      render: (_, { orgs }) => {
        if (orgs && orgs.length) {
          return orgs[0].name;
        }
        return '';
      },
    },
    {
      title: 'Measured Events',
      dataIndex: 'meassuredEvents',
      key: 'meassuredEvents',
      width: 500,
      render: (_, { transactions, signups, sessions, tagUpdateDate }) => {
        const events = [
          {
            name: 'Transactions',
            value: transactions,
            date: tagUpdateDate,
          },
          {
            name: 'Signups',
            value: signups,
            date: tagUpdateDate,
          },
          {
            name: 'Sessions',
            value: sessions,
            date: tagUpdateDate,
          },
        ];

        return events
          .filter((event) => event.value)
          .map((event) => (
            <Space key={event.name} size="middle">
              <Tag color={colorPicker(event.date, event.value)}>
                <Space direction="vertical" size={0}>
                  <Text type={typePicker(event.date, event.value)} strong>
                    {event.name}
                  </Text>
                  <Text type={typePicker(event.date, event.value)} strong>
                    {formatWholeNumberNaN(event.value)}
                  </Text>
                  <Text type={typePicker(event.date, event.value)} disabled>
                    {event.date
                      ? moment(event.date).format('YYYY/MM/DD HH:mm:ss')
                      : 'Signal Unknown'}
                  </Text>
                </Space>
              </Tag>
            </Space>
          ));
      },
    },
    {
      title: 'Tag ID',
      dataIndex: 'tagId',
      key: 'tagId',
      render: (_, { eventTags }) => {
        if (eventTags && eventTags.length) {
          return eventTags[0].appId;
        }
        return '';
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      render: (record) => {
        const tagId = record.id;
        const appId =
          record.eventTags && record.eventTags.length ? record.eventTags[0].appId : null;
        const orgId = record.orgs && record.orgs.length ? record.orgs[0].id : null;

        return (
          <div style={{ marginTop: -14 }}>
            <Button
              color="primary"
              variant="filled"
              icon={<TagOutlined />} // Added tag icon
              onClick={async (event) => {
                event.stopPropagation();
                if (appId && orgId && tagId) {
                  handleTestSignal({ appId, orgId, tagId });
                }
              }}
            >
              Test Signal
            </Button>
          </div>
        );
      },
    },
  ];

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleAddSelections = () => {
    if (selectedRowKeys.length === 0) {
      onAddSelections(null);
      setVisible(false);
      return;
    }
    const eventTarget = tags.filter((tag) => selectedRowKeys.includes(tag.id))[0];
    onAddSelections(eventTarget);
    setVisible(false);
  };

  const rowSelection = {
    type: 'radio',
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: !record[eventType],
    }),
  };

  const onFetchTags = () => {
    fetchTags({
      variables: {
        where: {
          OR: [
            {
              orgs_some: {
                id: form.getFieldValue('advertiserId'),
              },
            },
          ],
          AND: [
            {
              OR: [
                {
                  isDisabled: false,
                },
                {
                  isDisabled: null,
                },
              ],
            },
          ],
        },
      },
    });
  };

  useEffect(() => {
    onFetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (basicTagActivity) {
      onFetchTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicTagActivity]);

  return (
    <>
      <Modal
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingRight: 20,
            }}
          >
            <span>Tags</span>
            <div>
              <Button type="default" onClick={() => handleAddSelections()}>
                Add Selection(s)
              </Button>
            </div>
          </div>
        }
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={1400}
      >
        <Spin spinning={loadingTags || tagActivityLoading}>
          <Table rowKey="id" rowSelection={rowSelection} columns={columns} dataSource={tags.filter((t) => {
              return t.sessions > 0 || t.transactions > 0 || t.signups > 0;})} />
        </Spin>
      </Modal>
    </>
  );
};

export default CampaignRequestorTagsDialog;
